import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, FileText, Shield, Clock, Mail, User, AlertCircle, BarChart3, MapPin, Euro, Plus, Gift, Trash2, Search, Coins, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SEOPanel from '@/components/SEOPanel';
import SecurityAuditPanel from '@/components/SecurityAuditPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [vipList, setVipList] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [activeTab, setActiveTab] = useState('simulations');
  
  // VIP Guest form state
  const [showAddVip, setShowAddVip] = useState(false);
  const [newVipEmail, setNewVipEmail] = useState('');
  const [newVipName, setNewVipName] = useState('');
  // Permanent VIP form state
  const [showAddPermVip, setShowAddPermVip] = useState(false);
  const [newPermVipEmail, setNewPermVipEmail] = useState('');
  const [newPermVipName, setNewPermVipName] = useState('');
  const [newPermVipIsAdmin, setNewPermVipIsAdmin] = useState(false);
  const [newPermVipNotes, setNewPermVipNotes] = useState('');

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await axiosInstance.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      if (!response.data.is_super_admin) {
        toast.error('Accès refusé - Réservé aux administrateurs');
        navigate('/dashboard');
        return;
      }
      
      setCurrentUser(response.data);
      loadAdminData();
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/');
    }
  };

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [logsRes, usersRes, vipRes, simsRes, tokensRes] = await Promise.all([
        axiosInstance.get(`${API}/admin/access-logs`, { withCredentials: true }),
        axiosInstance.get(`${API}/admin/users`, { withCredentials: true }),
        axiosInstance.get(`${API}/admin/vip-list`, { withCredentials: true }),
        axiosInstance.get(`${API}/admin/simulations`, { withCredentials: true }),
        axiosInstance.get(`${API}/admin/token-usage`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      setAccessLogs(logsRes.data);
      setUsers(usersRes.data);
      setVipList(vipRes.data.vip_users);
      setSimulations(simsRes.data);
      setTokenUsage(tokensRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const addVipGuest = async () => {
    if (!newVipEmail || !newVipName) {
      toast.error('Email et nom requis');
      return;
    }
    
    try {
      const response = await axiosInstance.post(`${API}/admin/vip-guests`, {
        email: newVipEmail,
        name: newVipName
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      setNewVipEmail('');
      setNewVipName('');
      setShowAddVip(false);
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const addCredits = async (email) => {
    try {
      const response = await axiosInstance.post(`${API}/admin/vip-guests/${encodeURIComponent(email)}/add-credits`, {}, {
        withCredentials: true
      });
      
      toast.success(response.data.message);
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de crédits');
    }
  };

  const removeVipGuest = async (email) => {
    if (!window.confirm(`Supprimer l'invité VIP ${email} ?`)) return;
    
    try {
      await axiosInstance.delete(`${API}/admin/vip-guests/${encodeURIComponent(email)}`, {
        withCredentials: true
      });
      
      toast.success('Invité VIP supprimé');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // ----- Permanent VIP CRUD -----
  const addPermanentVip = async () => {
    if (!newPermVipEmail || !newPermVipName) {
      toast.error('Email et nom requis');
      return;
    }
    try {
      const response = await axiosInstance.post(`${API}/admin/permanent-vips`, {
        email: newPermVipEmail.trim().toLowerCase(),
        name: newPermVipName.trim(),
        is_super_admin: newPermVipIsAdmin,
        notes: newPermVipNotes.trim(),
      }, { withCredentials: true });
      toast.success(response.data.message);
      setNewPermVipEmail('');
      setNewPermVipName('');
      setNewPermVipIsAdmin(false);
      setNewPermVipNotes('');
      setShowAddPermVip(false);
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const removePermanentVip = async (email) => {
    if (!window.confirm(`Supprimer le VIP permanent ${email} ?\n\nIl perdra son accès illimité.`)) return;
    try {
      await axiosInstance.delete(`${API}/admin/permanent-vips/${encodeURIComponent(email)}`, {
        withCredentials: true,
      });
      toast.success('VIP permanent supprimé');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const togglePermanentVipAdmin = async (vip) => {
    try {
      await axiosInstance.put(`${API}/admin/permanent-vips/${encodeURIComponent(vip.email)}`, {
        is_super_admin: !vip.is_super_admin,
      }, { withCredentials: true });
      toast.success(`Droits ${!vip.is_super_admin ? 'super admin accordés' : 'super admin retirés'}`);
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="sm"
                className="text-slate-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Administration</h1>
                <p className="text-sm text-slate-500">Super Admin: {currentUser?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full">
              <Shield className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-600 font-semibold">Super Admin</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white p-1 rounded-lg border border-slate-200 inline-flex flex-wrap">
          <button
            onClick={() => setActiveTab('simulations')}
            data-testid="tab-simulations"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'simulations'
                ? 'bg-blue-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <BarChart3 className="h-4 w-4 inline mr-2" />
            Simulations ({simulations.length})
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            data-testid="tab-logs"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'logs'
                ? 'bg-blue-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Clock className="h-4 w-4 inline mr-2" />
            Logs d'accès
          </button>
          <button
            onClick={() => setActiveTab('users')}
            data-testid="tab-users"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Utilisateurs ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('vip')}
            data-testid="tab-vip"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'vip'
                ? 'bg-blue-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Shield className="h-4 w-4 inline mr-2" />
            Liste VIP ({vipList.length})
          </button>
          <button
            onClick={() => setActiveTab('seo')}
            data-testid="tab-seo"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'seo'
                ? 'bg-green-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Search className="h-4 w-4 inline mr-2" />
            SEO
          </button>
          <button
            onClick={() => setActiveTab('tokens')}
            data-testid="tab-tokens"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'tokens'
                ? 'bg-amber-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Coins className="h-4 w-4 inline mr-2" />
            Tokens IA
          </button>
          <button
            onClick={() => setActiveTab('security')}
            data-testid="tab-security"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'security'
                ? 'bg-purple-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <ShieldAlert className="h-4 w-4 inline mr-2" />
            Sécurité
          </button>
        </div>

        {/* Simulations History */}
        {activeTab === 'simulations' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Historique des Simulations</h2>
              <p className="text-sm text-slate-500">{simulations.length} simulation(s) effectuée(s)</p>
            </div>
            {simulations.length === 0 ? (
              <div className="p-12 text-center">
                <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune simulation effectuée</p>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="simulations-table">
                {/* Grid layout (instead of <table>) to avoid hydration warnings
                    caused by Emergent's x-ve-dynamic wrappers around iterated <tr> */}
                <div className="min-w-[1100px]">
                  {/* Header row */}
                  <div className="grid grid-cols-7 gap-0 bg-slate-50 border-b border-slate-200">
                    <div className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</div>
                    <div className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Utilisateur</div>
                    <div className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Localisation</div>
                    <div className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Prix</div>
                    <div className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cash-flow Trad.</div>
                    <div className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cash-flow Airbnb</div>
                    <div className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">VIP</div>
                  </div>
                  {/* Body rows */}
                  <div className="divide-y divide-slate-200">
                    {simulations.map((sim, index) => (
                      <div
                        key={sim.simulation_id || sim._id || index}
                        className="grid grid-cols-7 gap-0 hover:bg-slate-50"
                        data-testid={`simulation-row-${index}`}
                      >
                        <div className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {new Date(sim.created_at).toLocaleString('fr-FR')}
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-800">{sim.user_email}</div>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-700">{sim.location || sim.city}</span>
                          </div>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Euro className="h-4 w-4 text-slate-400" />
                            <span className="text-sm font-medium text-slate-800">
                              {sim.property_price?.toLocaleString() || '—'}
                            </span>
                          </div>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${
                            (sim.cashflow_traditional || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {sim.cashflow_traditional?.toFixed(0) || '—'} €/mois
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${
                            (sim.cashflow_airbnb || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {sim.cashflow_airbnb?.toFixed(0) || '—'} €/mois
                          </span>
                        </div>
                        <div className="px-6 py-4 whitespace-nowrap">
                          {sim.is_vip ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              VIP
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Access Logs */}
        {activeTab === 'logs' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Logs d'accès VIP</h2>
              <p className="text-sm text-slate-500">Historique des connexions VIP</p>
            </div>
            {accessLogs.length === 0 ? (
              <div className="p-12 text-center">
                <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun log d'accès disponible</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Admin
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {accessLogs.map((log, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {new Date(log.timestamp).toLocaleString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-800 font-medium">
                          {log.user_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {log.is_super_admin ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              Admin
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Users List */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Tous les utilisateurs</h2>
              <p className="text-sm text-slate-500">{users.length} utilisateur(s) inscrit(s)</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Inscrit le
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {users.map((user, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img
                              src={user.picture}
                              alt={user.name}
                              className="h-8 w-8 rounded-full"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="h-4 w-4 text-slate-500" />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-slate-800">{user.name}</div>
                            <div className="text-xs text-slate-500">{user.first_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {user.is_vip && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                              VIP
                            </span>
                          )}
                          {user.is_super_admin && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              Admin
                            </span>
                          )}
                          {!user.is_vip && !user.is_super_admin && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                              Standard
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* VIP List */}
        {activeTab === 'vip' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Gestion des VIP</h2>
                <p className="text-sm text-slate-500">VIP permanents et invités avec crédits</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowAddPermVip(!showAddPermVip)}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="add-permanent-vip-btn"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Ajouter un VIP permanent
                </Button>
                <Button
                  onClick={() => setShowAddVip(!showAddVip)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="add-vip-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un invité VIP
                </Button>
              </div>
            </div>
            
            {/* Add Permanent VIP Form */}
            {showAddPermVip && (
              <div className="p-6 bg-amber-50 border-b border-amber-200" data-testid="add-permanent-vip-form">
                <h3 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5" /> Nouveau VIP permanent (accès illimité)
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={newPermVipEmail}
                      onChange={(e) => setNewPermVipEmail(e.target.value)}
                      placeholder="email@exemple.com"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      data-testid="new-perm-vip-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet *</label>
                    <input
                      type="text"
                      value={newPermVipName}
                      onChange={(e) => setNewPermVipName(e.target.value)}
                      placeholder="Jean Dupont"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      data-testid="new-perm-vip-name"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optionnel)</label>
                  <input
                    type="text"
                    value={newPermVipNotes}
                    onChange={(e) => setNewPermVipNotes(e.target.value)}
                    placeholder="Raison, contexte, partenariat..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="new-perm-vip-notes"
                  />
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPermVipIsAdmin}
                      onChange={(e) => setNewPermVipIsAdmin(e.target.checked)}
                      className="h-4 w-4 text-amber-600 rounded"
                      data-testid="new-perm-vip-is-admin"
                    />
                    <span className="text-sm text-slate-700">Donner les droits Super Admin</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={addPermanentVip} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-add-permanent-vip">
                    Créer le VIP permanent
                  </Button>
                  <Button onClick={() => setShowAddPermVip(false)} variant="outline">Annuler</Button>
                </div>
              </div>
            )}
            
            {/* Add VIP Form */}
            {showAddVip && (
              <div className="p-6 bg-green-50 border-b border-green-200">
                <h3 className="font-semibold text-green-800 mb-4">Nouvel invité VIP (10 crédits offerts)</h3>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newVipEmail}
                      onChange={(e) => setNewVipEmail(e.target.value)}
                      placeholder="email@exemple.com"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      data-testid="new-vip-email"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet</label>
                    <input
                      type="text"
                      value={newVipName}
                      onChange={(e) => setNewVipName(e.target.value)}
                      placeholder="Jean Dupont"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      data-testid="new-vip-name"
                    />
                  </div>
                  <Button onClick={addVipGuest} className="bg-green-600 hover:bg-green-700" data-testid="confirm-add-vip">
                    Ajouter
                  </Button>
                  <Button onClick={() => setShowAddVip(false)} variant="outline">
                    Annuler
                  </Button>
                </div>
              </div>
            )}
            
            <div className="p-6">
              {/* Permanent VIPs */}
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-500" />
                VIP Permanents (accès illimité)
              </h3>
              <div className="grid gap-3 mb-8" data-testid="permanent-vips-list">
                {vipList.filter(v => v.type === 'permanent').map((vip, index) => (
                  <div 
                    key={vip.email || index}
                    className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200"
                    data-testid={`permanent-vip-row-${vip.email}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{vip.name}</div>
                        <div className="text-sm text-slate-500">{vip.email}</div>
                        {vip.notes && (
                          <div className="text-xs text-slate-400 italic mt-1">{vip.notes}</div>
                        )}
                        {vip.tags && vip.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vip.tags.map((tag, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                        ∞ Illimité
                      </span>
                      {vip.is_super_admin && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                          Super Admin
                        </span>
                      )}
                      {/* Super admin toggle — hidden on the main super admin to avoid self-locking */}
                      {vip.email !== currentUser?.email && (
                        <Button
                          onClick={() => togglePermanentVipAdmin(vip)}
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          data-testid={`toggle-admin-${vip.email}`}
                          title={vip.is_super_admin ? "Retirer les droits super admin" : "Donner les droits super admin"}
                        >
                          {vip.is_super_admin ? '↓ Admin' : '↑ Admin'}
                        </Button>
                      )}
                      {vip.email !== currentUser?.email && (
                        <Button
                          onClick={() => removePermanentVip(vip.email)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-permanent-vip-${vip.email}`}
                          title="Supprimer ce VIP permanent"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* VIP Guests */}
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Gift className="h-5 w-5 text-blue-500" />
                Invités VIP (avec crédits)
              </h3>
              {vipList.filter(v => v.type === 'guest').length === 0 ? (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                  <Gift className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                  <p>Aucun invité VIP pour le moment</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {vipList.filter(v => v.type === 'guest').map((vip, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Mail className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{vip.name}</div>
                          <div className="text-sm text-slate-500">{vip.email}</div>
                          {vip.created_at && (
                            <div className="text-xs text-slate-400">
                              Ajouté le {new Date(vip.created_at).toLocaleDateString('fr-FR')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center px-4">
                          <div className={`text-2xl font-bold ${vip.credits > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {vip.credits}
                          </div>
                          <div className="text-xs text-slate-500">crédits</div>
                        </div>
                        <Button
                          onClick={() => addCredits(vip.email)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`add-credits-${index}`}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          +5 crédits
                        </Button>
                        <Button
                          onClick={() => removeVipGuest(vip.email)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`remove-vip-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SEO Panel */}
        {activeTab === 'seo' && (
          <SEOPanel />
        )}

        {/* Token Usage Panel */}
        {activeTab === 'tokens' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Consommation Tokens IA</h2>
              <p className="text-sm text-slate-500">Suivi des coûts d'utilisation de l'IA (GPT-5.2)</p>
            </div>
            
            {tokenUsage ? (
              <div className="p-6 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200">
                    <div className="flex items-center gap-3 mb-2">
                      <Coins className="h-8 w-8 text-amber-600" />
                      <div>
                        <p className="text-sm text-amber-700">Total Tokens</p>
                        <p className="text-2xl font-bold text-amber-900">
                          {tokenUsage.total_tokens?.toLocaleString() || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                    <div className="flex items-center gap-3 mb-2">
                      <Euro className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm text-green-700">Coût Total</p>
                        <p className="text-2xl font-bold text-green-900">
                          {tokenUsage.total_cost_eur?.toFixed(4) || '0.0000'} €
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                    <div className="flex items-center gap-3 mb-2">
                      <BarChart3 className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-sm text-blue-700">Requêtes IA</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {tokenUsage.recent_usage?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage by Feature */}
                {tokenUsage.by_feature && Object.keys(tokenUsage.by_feature).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-3">Par fonctionnalité</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(tokenUsage.by_feature).map(([feature, data]) => (
                        <div key={feature} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <p className="text-sm font-medium text-slate-700 capitalize">
                            {feature.replace(/_/g, ' ')}
                          </p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-slate-500">{data.count} appel(s)</span>
                            <span className="text-sm font-semibold text-green-600">{data.cost_eur?.toFixed(4)} €</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{data.tokens?.toLocaleString()} tokens</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Usage by User */}
                {tokenUsage.by_user && Object.keys(tokenUsage.by_user).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-3">Par utilisateur</h3>
                    <div className="space-y-2">
                      {Object.entries(tokenUsage.by_user).map(([email, data]) => (
                        <div key={email} className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center gap-3">
                            <User className="h-5 w-5 text-slate-400" />
                            <span className="text-sm text-slate-700">{email}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-slate-500">{data.count} requête(s)</span>
                            <span className="text-sm font-semibold text-green-600">{data.cost_eur?.toFixed(4)} €</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Usage */}
                {tokenUsage.recent_usage && tokenUsage.recent_usage.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-3">Dernières utilisations</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Utilisateur</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Feature</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-slate-500">Tokens</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-slate-500">Coût</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {tokenUsage.recent_usage.slice(0, 20).map((usage, i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="px-4 py-2 text-slate-600">
                                {new Date(usage.created_at).toLocaleString('fr-FR', { 
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                                })}
                              </td>
                              <td className="px-4 py-2 text-slate-700">{usage.user_email}</td>
                              <td className="px-4 py-2 text-slate-600 capitalize">{usage.feature?.replace(/_/g, ' ')}</td>
                              <td className="px-4 py-2 text-right text-slate-600">{usage.total_tokens?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right font-medium text-green-600">{usage.cost_eur?.toFixed(4)} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Coins className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune donnée de consommation disponible</p>
                <p className="text-sm text-slate-400 mt-2">Les tokens seront comptabilisés lors de l'utilisation des fonctionnalités IA (SEO)</p>
              </div>
            )}
          </div>
        )}

        {/* Security Audit Panel */}
        {activeTab === 'security' && (
          <SecurityAuditPanel />
        )}
      </main>
    </div>
  );
}
